import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Badge, Button, Callout, Card, Flex, Grid, Heading, Spinner, Tabs, Text } from "@radix-ui/themes";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { resetUpdateAcademicPeriodData } from "../../redux/slices/school/academicPeriod";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { updateAcademicPeriod } from "../../services/school/academicPeriod/updateAcademicPeriod";
import { AcademicPeriodType } from "../../types/AcademicPeriod";
import { UpdateAcademicPeriodFormValidation } from "./UpdateAcademicPeriodFormValidation";
import { SchoolCalendar } from "../../components/common/SchoolCalendar";
import { useState } from "react";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
 
type ActiveSegmentTab  ='Form'|'Preview';

export const UpdateAcademicPeriod = () => {

    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('Form');

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const mutation = useMutation({ mutationFn: updateAcademicPeriod, mutationKey: 'updateAcademicPeriod' });
    const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear, } = useGetCurrentAcademicYearQuery();
    const updateAcademicPeriodFormData = useSelector((state: RootState) => state.academicPeriod);

    const defaultValues: AcademicPeriodType = updateAcademicPeriodFormData;
    const { control, handleSubmit, setValue, getValues, formState: { errors },watch ,trigger} = useForm<AcademicPeriodType>({
        resolver: yupResolver(UpdateAcademicPeriodFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
        trigger();
    }
    if (!currentAcademicYear || !currentAcademicYear?.status) {
        return (
            <Flex direction="column">
                <Callout.Root color="red" role="alert">
                    <Callout.Icon>
                        <ExclamationTriangleIcon />
                    </Callout.Icon>
                    <Callout.Text>
                        Por favor active o ano lectivo.{" "}
                        <Link to={routes.academicPeriods} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
                    </Callout.Text>
                </Callout.Root>

            </Flex>
        );
    }
    const handleBack = () => {
        navigate(routes.academicPeriods);
        dispatch(resetUpdateAcademicPeriodData());
    }
    const onSubmit = handleSubmit(async (data) => {
        try {
            const result = await mutation.mutateAsync(data);
            if (result) {
                toast.success("Actualizado!");
                handleBack();
            }
        } catch (err: any) {
            toast.error(`${mutation.error ?? err?.message}`);
        }
    });

    const watchedValues = watch(); 
    const formatEvents = () => {
        const values = watchedValues;
        return [
            {
                title: "1º Semestre",
                start: new Date(values.startFirstSemester),
                end: new Date(values.endFirstSemester),
            },
            {
                title: "2º Semestre",
                start: new Date(values.startSecondSemester),
                end: new Date(values.endSecondSemester),
            },
            {
                title: "Exame de Admissão",
                start: new Date(values.startAdmissionExam),
                end: new Date(values.endAdmissionExam),
            },
            {
                title: "Exame Normal",
                start: new Date(values.startNormalExam),
                end: new Date(values.endNormalExam),
            },
            {
                title: "Exame de Recorrência",
                start: new Date(values.startRecurrentExam),
                end: new Date(values.endRecurrentExam),
            },
            {
                title: "Período de Matrículas",
                start: new Date(values.startEnrollment),
                end: new Date(values.endEnrollment),
            },
        ];
    };
    return (
        <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
            navigateAndValidate(tabToNavigate as ActiveSegmentTab);
        }}>
   <Tabs.List>
                    <Tabs.Trigger value="Form">Semestres  <Badge color="red">{Object.keys(errors)?.length} </Badge> </Tabs.Trigger>
                    <Tabs.Trigger value="Preview">Visualizar  </Tabs.Trigger>
                </Tabs.List>
              
            <Tabs.Content value="Form">
                <Card className="p-6">
                    <form onSubmit={onSubmit} noValidate>
                        <Flex direction='column' gap='4'>
                            <Grid columns="2" gap="4" className="mb-6">
                                <Card className="p-4">
                                    <Heading size="3" className="mb-4">Semestres</Heading>
                                    <Grid columns="2" gap="4">
                                        <DateFieldComponent name="startFirstSemester" label='Início do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endFirstSemester" label='Fim do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="startSecondSemester" label='Início do 2º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endSecondSemester" label='Fim do 2º Semestre' control={control} errors={errors} required />
                                    </Grid>
                                </Card>
                                <Card className="p-4">
                                    <Heading size="3" className="mb-4">Exames</Heading>
                                    <Grid columns="2" gap="4">
                                        <DateFieldComponent name="startAdmissionExam" label='Início Exame Admissão' control={control} errors={errors} required />
                                        <DateFieldComponent name="endAdmissionExam" label='Fim Exame Admissão' control={control} errors={errors} required />
                                        <DateFieldComponent name="startNormalExam" label='Início Exame Normal do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endNormalExam" label='Fim Exame Normal do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="startNormalExamSecondSemester" label='Início Exame Normal do 2º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endNormalExamSecondSemester" label='Fim Exame Normal do 2º Semestre' control={control} errors={errors} required />

                                        
                                        <DateFieldComponent name="startRecurrentExam" label='Início Exame de Recorrência do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endRecurrentExam" label='Fim Exame de Recorrência do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="startRecurrentExamSecondSemester" label='Início Exame de Recorrência do 2º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endRecurrentExamSecondSemester" label='Fim Exame de Recorrência do 2º Semestre' control={control} errors={errors} required />
                                    </Grid>
                                </Card>
                                <Card className="p-4">
                                    <Heading size="3" className="mb-4">Matrículas</Heading>
                                    <Grid columns="2" gap="4">
                                        <DateFieldComponent name="startEnrollment" label='Início das Matrículas' control={control} errors={errors} required />
                                        <DateFieldComponent name="endEnrollment" label='Fim das Matrículas' control={control} errors={errors} required />
                                    </Grid>
                                </Card>
                                <Card className="p-4">
                                    <Heading size="3" className="mb-4">Inscrições</Heading>
                                    <Grid columns="2" gap="4">
                                        <DateFieldComponent name="startSubscription" label='Início das Inscrições do 1º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endSubscription" label='Fim das Inscrições do 1º Semestre' control={control} errors={errors} required />
                                    </Grid>
                                    <Grid columns="2" gap="4">
                                        <DateFieldComponent name="startSubscriptionSecondSemester" label='Início das Inscrições do 2º Semestre' control={control} errors={errors} required />
                                        <DateFieldComponent name="endSubscriptionSecondSemester" label='Fim das Inscrições do 2º Semestre' control={control} errors={errors} required />
                                    </Grid>
                                </Card>
                            </Grid>
                            <RadioGroupComponent name="forceUsePeriod" label="Forçar Período" control={control} errors={errors} required options={[{ value: "true", label: "Sim" }, { value: "false", label: "Não" }]} />
                            <Flex justify="end" gap='4'>
                                <Button type="button" variant="soft" color="gray" onClick={() => navigate(routes.academicPeriods)} disabled={mutation.isLoading}><ChevronLeftIcon /> Voltar</Button>
                                <Button type="submit" disabled={mutation.isLoading} variant='surface'>
                                    {mutation.isLoading ? <Spinner loading><EnterIcon /></Spinner> : <EnterIcon />}
                                    Actualizar
                                </Button>
                            </Flex>
                        </Flex>
                    </form>
                </Card>
            </Tabs.Content>
                <Tabs.Content  value="Preview" >

                <Card className="p-4">
                    <Heading size="3" className="mb-4">Calendário Académico</Heading>
                        <SchoolCalendar events={formatEvents()}/>
                        </Card>
                </Tabs.Content>

        </Tabs.Root>
      
    )
}